/* eslint-disable no-console */
import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';

import AirportApi from 'api/AirportApi';
import ConnectivityAPI from 'api/ConnectivityAPI';
import LocationApi from 'api/LocationApi';
import LogApi from 'api/LogApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';
import { Today } from 'utils/dateTimeHelpers';

import { GeonameModelWithCode, LocationSearchType } from 'components/_controls/AoiSearch/models';

import * as ConnectivitySelectors from 'connectivity/connectivity.selectors';

import {
  ConnectivityGroupByRequest,
  ConnectivityODLocationRequest,
  ConnectivityToggleRequestType,
  DefaultConnectivityGroupBy,
  DefaultMopSort,
  ModBulkUpdateRequest,
  ModODPageSize,
  MopBulkUpdateRequest,
  MopODPageSize,
} from './connectivity.models';
import {
  appendODInfo,
  appendPaginatedDiseaseRisks,
  errorLoadingData,
  errorLoadingDiseaseRisks,
  errorLoadingModAggRow,
  errorLoadingPaginationData,
  resetTravelVolumeInfo,
  setActiveGroupBy,
  setActiveModSort,
  setActiveMopSort,
  setDiseaseRisks,
  setFilteredDiseases,
  setGroupByDisclaimerStatus,
  setIncludeDomestic,
  setModAggRowDiseases,
  setModAggRowInfo,
  setODInfo,
  setODLocations,
  startLoadingLocations,
  startLoadingModAggRow,
  startLoadingDiseaseRisks,
  startLoadingTravelVolumes,
  startRefreshingDiseaseRisks,
  startRefreshingTravelVolumes,
  toggleIncludeDomestic,
  resetDiseaseRiskInfo,
  setIncludeGroundTravel,
  toggleIncludeGroundTravel,
  setIsGroundTravelAllowed,
} from './connectivity.slice';

export const fetchConnectivityLocationInfo = createAction<ConnectivityODLocationRequest>(
  'connectivity/fetchLocationInfo'
);
export const fetchMopODInfo = createAction('connectivity/fetchMopODInfo');
export const fetchPaginatedMopODInfo = createAction('connectivity/fetchPaginatedMopODInfo');
export const updateMopSort = createAction<dto.TravelVolumeSortType>('connectivity/updateMopSort');
export const updateMopGroupBy = createAction<ConnectivityGroupByRequest>(
  'connectivity/updateMopGroupBy'
);
export const toggleMopGroundTravel = createAction<boolean>('connectivity/toggleMopGroundTravel');
export const bulkUpdateMopSettings = createAction<MopBulkUpdateRequest>(
  'connectivity/bulkUpdateMopSettings'
);

export const toggleDomestic = createAction<ConnectivityToggleRequestType>(
  'connectivity/toggleDomestic'
);

export const fetchDiseaseRisk = createAction<boolean>('connectivity/fetchDiseaseRisk');
export const fetchPaginatedDiseaseRisk = createAction('connectivity/fetchPaginatedDiseaseRisk');
export const updateModGroupBy = createAction<ConnectivityGroupByRequest>(
  'connectivity/updateModGroupBy'
);
export const updateModSort = createAction<dto.DiseaseMovementSortType>(
  'connectivity/updateModSort'
);
export const updateFilteredDiseases = createAction<number[]>(
  'connectivity/updatedFilteredDiseases'
);
export const updateFilterAndDomestic = createAction<{ diseaseIds: number[]; isDomestic: boolean }>(
  'connectivity/updateFilterAndDomestic'
);
export const bulkUpdateModSettings = createAction<ModBulkUpdateRequest>(
  'connectivity/bulkUpdateModSettings'
);

const showServerError = put(
  showNotification({
    type: 'SHOW_ERROR_NOTIFICATION',
    payload: 'Unexpected internal server error. Please try again',
  })
);

const shouldShowGroupByDisclaimer = ({
  destinationGroupBy,
  originGroupBy,
}: ConnectivityGroupByRequest) =>
  destinationGroupBy === dto.ConnectivityGroupByType.Province ||
  destinationGroupBy === dto.ConnectivityGroupByType.County ||
  originGroupBy === dto.ConnectivityGroupByType.Province ||
  originGroupBy === dto.ConnectivityGroupByType.County;

function* queryTravelVolumes() {
  const originId = yield select(ConnectivitySelectors.getOriginLocationId);
  const destinationId = yield select(ConnectivitySelectors.getDestinationLocationId);
  const sortBy = yield select(ConnectivitySelectors.activeMopSortSelector);

  const originGroupBy = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
  const destinationGroupBy = yield select(ConnectivitySelectors.activeDestinationGroupBySelector);
  const includeDomestic = yield select(ConnectivitySelectors.isDomesticIncluded);
  const includeGroundTravel = yield select(ConnectivitySelectors.isGroundTravelIncludedSelector);

  const travelVolRes: dto.TravelVolumeListModel = yield call(ConnectivityAPI.getTravelVolume, {
    sourceLocationId: originId,
    sourceGroupBy: originGroupBy,
    destinationLocationId: destinationId,
    destinationGroupBy: destinationGroupBy,
    pageSize: MopODPageSize,
    sortBy: sortBy,
    includeDomestic: includeDomestic,
    skipRows: 0,
    includeAirTravel: true,
    includeGroundTravel: includeGroundTravel,
  });

  yield put(setODInfo(travelVolRes));
}

function* handleShowGroupByDisclaimer(request: ConnectivityGroupByRequest) {
  if (shouldShowGroupByDisclaimer(request)) {
    yield put(setGroupByDisclaimerStatus('shown'));
  } else {
    yield put(setGroupByDisclaimerStatus('hidden'));
  }
}

function* setMopGroupBysToGroundDefault() {
  let originGroupBy = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
  let destinationGroupBy = yield select(ConnectivitySelectors.activeDestinationGroupBySelector);

  // Since airports aren't able to have ground travel, we need to change the groupbys to the lowest non-airport if they are airport
  if (originGroupBy === dto.ConnectivityGroupByType.Airport) {
    const opts = yield select(ConnectivitySelectors.getOriginGroupByOptions);
    originGroupBy = opts.length === 1 ? opts[0].value : opts[1].value; // fallback incase if airport is the queried location
  }
  if (destinationGroupBy === dto.ConnectivityGroupByType.Airport) {
    const opts = yield select(ConnectivitySelectors.getDestinationGroupByOptions);
    destinationGroupBy = opts.length === 1 ? opts[0].value : opts[1].value;
  }

  const newGroupBy: ConnectivityGroupByRequest = { originGroupBy, destinationGroupBy };

  yield put(setActiveGroupBy(newGroupBy));
  yield handleShowGroupByDisclaimer(newGroupBy);
}

export function* fetchConnectivityLocationInfoSaga(
  action: PayloadAction<ConnectivityODLocationRequest>
) {
  try {
    const { destinationId, originId } = action.payload;

    const queryIds =
      destinationId != null && originId != null
        ? [destinationId, originId]
        : originId != null
        ? [originId]
        : destinationId != null
        ? [destinationId]
        : null;

    yield put(
      setActiveGroupBy({
        originGroupBy: DefaultConnectivityGroupBy,
        destinationGroupBy: DefaultConnectivityGroupBy,
      })
    );
    yield put(setGroupByDisclaimerStatus('hidden'));
    yield put(setIncludeGroundTravel(false));

    if (queryIds != null) {
      yield put(startLoadingLocations());

      const airports: GeonameModelWithCode[] = (yield all(
        queryIds.map(x => call(AirportApi.getAirportInformation, x))
      ))
        .filter((x: dto.GetAirportModel) => !!x.id)
        .map((x: dto.GetAirportModel) => {
          return {
            geonameId: x.id,
            name: x.name,
            fullDisplayName: x.displayName,
            province: x.province,
            country: x.country,
            locationType: LocationSearchType.Airport,
            code: x.code,
            countryId: x.countryId,
          } as GeonameModelWithCode;
        });

      const airportIds = airports.map(x => x.geonameId);
      const remainingIds = queryIds.filter(x => !airportIds.includes(x));

      const locRes: GeonameModelWithCode[] = yield call(LocationApi.getGeoname, {
        geonameIds: remainingIds,
        includeShape: false,
      });

      const fullRes = [...(airports ?? []), ...locRes];
      const origin = fullRes.find(d => d.geonameId === originId);
      const destination = fullRes.find(d => d.geonameId === destinationId);

      // TODO: df248ed6 The origin/destination country querying can be removed when counties get expanded beyond the US
      const countryIds = [];

      if (origin != null && origin.geonameId !== origin.countryId)
        countryIds.push(origin.countryId);
      if (destination != null && destination.geonameId !== destination.countryId)
        countryIds.push(destination.countryId);

      let originCountryHasCounty = false;
      let destinationCountryHasCounty = false;

      if (countryIds.length) {
        const countryLocRes: GeonameModelWithCode[] = yield call(LocationApi.getGeoname, {
          geonameIds: countryIds,
          includeShape: false,
        });

        const originCountry = countryLocRes.find(d => d.geonameId === origin?.countryId);
        const destinationCountry = countryLocRes.find(d => d.geonameId === destination?.countryId);

        originCountryHasCounty = originCountry?.hasCounties;
        destinationCountryHasCounty = destinationCountry?.hasCounties;
      }

      // END TODO remove section (post county expand)

      yield put(
        setODLocations({
          origin:
            origin != null
              ? {
                  id: originId,
                  displayName: origin.fullDisplayName,
                  name: origin.name,
                  locationType: origin.locationType,
                  countryName: origin.country,
                  provinceName: origin.province,
                  airportCode: origin.code,
                  countryId: origin.countryId,
                  hasCounties: origin.hasCounties || originCountryHasCounty, // TODO: df248ed6 remove post '||' when counties gets expanded beyond the US
                  hasProvinces: origin.hasProvinces,
                }
              : null,
          destination:
            destination != null
              ? {
                  id: destinationId,
                  displayName: destination.fullDisplayName,
                  name: destination.name,
                  locationType: destination.locationType,
                  countryName: destination.country,
                  provinceName: destination.province,
                  airportCode: destination.code,
                  countryId: destination.countryId,
                  hasCounties: destination.hasCounties || destinationCountryHasCounty, // TODO: df248ed6 remove post '||' when counties gets expanded beyond the US
                  hasProvinces: destination.hasProvinces,
                }
              : null,
        })
      );
    } else {
      yield put(setODLocations({ origin: null, destination: null }));
    }
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
    yield put(errorLoadingDiseaseRisks());
    yield showServerError;
  }
}

export function* updateMopGroupBySaga(action: PayloadAction<ConnectivityGroupByRequest>) {
  try {
    yield put(startRefreshingTravelVolumes());
    yield put(setActiveGroupBy(action.payload));

    const isGroundIncluded = yield select(ConnectivitySelectors.isGroundTravelIncludedSelector);
    if (
      isGroundIncluded &&
      (action.payload.destinationGroupBy === dto.ConnectivityGroupByType.Airport ||
        action.payload.originGroupBy === dto.ConnectivityGroupByType.Airport)
    ) {
      yield put(setIncludeGroundTravel(false));
    }

    yield handleShowGroupByDisclaimer(action.payload);

    yield queryTravelVolumes();
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
    yield showServerError;
  }
}

export function* bulkUpdateMopSettingsSaga(action: PayloadAction<MopBulkUpdateRequest>) {
  try {
    yield put(startRefreshingTravelVolumes());
    yield put(setIncludeDomestic(action.payload.isDomesticIncluded));
    yield put(setIncludeGroundTravel(action.payload.isGroundIncluded));
    yield put(
      setActiveGroupBy({
        originGroupBy: action.payload.originGroupBy,
        destinationGroupBy: action.payload.destinationGroupBy,
      })
    );
    yield put(setActiveMopSort(action.payload.sort));

    yield queryTravelVolumes();
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
    yield showServerError;
  }
}

export function* updateMopSortSaga(action: PayloadAction<dto.TravelVolumeSortType>) {
  try {
    yield put(startRefreshingTravelVolumes());
    yield put(setActiveMopSort(action.payload));

    yield queryTravelVolumes();
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
    yield showServerError;
  }
}

export function* toggleMopGroundTravelSaga() {
  try {
    yield put(startRefreshingTravelVolumes());
    yield put(toggleIncludeGroundTravel());

    const isGroundIncluded = yield select(ConnectivitySelectors.isGroundTravelIncludedSelector);
    const activeMopSort = yield select(ConnectivitySelectors.activeMopSortSelector);

    if (isGroundIncluded) {
      yield put(setIncludeDomestic(true));
      yield setMopGroupBysToGroundDefault();
    } else if (activeMopSort === dto.TravelVolumeSortType.GroundVolumeHighToLow) {
      yield put(setActiveMopSort(DefaultMopSort));
    }

    yield delay(500);

    yield queryTravelVolumes();
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
    yield showServerError;
  }
}

export function* fetchMopODInfoSaga() {
  try {
    yield put(startLoadingTravelVolumes());
    yield put(resetTravelVolumeInfo());

    const originId = yield select(ConnectivitySelectors.getOriginLocationId);
    const destintationId = yield select(ConnectivitySelectors.getDestinationLocationId);

    const metadata: dto.TravelVolumeModel = yield call(
      ConnectivityAPI.getTravelVolumeMetadata,
      originId,
      destintationId
    );

    yield put(setIsGroundTravelAllowed(metadata?.isGroundTravelSupported));

    const isGroundIncluded = yield select(ConnectivitySelectors.isGroundTravelIncludedSelector);
    const isDomesticOnly = yield select(ConnectivitySelectors.isDomesticOnly);

    if (metadata?.isGroundTravelSupported && isDomesticOnly) {
      yield put(setIncludeGroundTravel(true));
      yield setMopGroupBysToGroundDefault();
    }
    // If ground was previously included and domestic is off or the group bys are airports, ground should be toggled off
    else if (isGroundIncluded) {
      const isDomesticIncluded = yield select(ConnectivitySelectors.isDomesticIncluded);
      const originGroupBy = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
      const destinationGroupBy = yield select(
        ConnectivitySelectors.activeDestinationGroupBySelector
      );

      if (
        !isDomesticIncluded ||
        originGroupBy === dto.ConnectivityGroupByType.Airport ||
        destinationGroupBy === dto.ConnectivityGroupByType.Airport
      ) {
        yield put(setIncludeGroundTravel(false));
      }
    }

    yield queryTravelVolumes();
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
    yield showServerError;
  }
}

export function* fetchPaginatedMopODInfoSaga() {
  try {
    yield put(startLoadingTravelVolumes());

    const originId = yield select(ConnectivitySelectors.getOriginLocationId);
    const destinationId = yield select(ConnectivitySelectors.getDestinationLocationId);
    const numOfVisibleRows = yield select(ConnectivitySelectors.getNumOfVisibileTravelVols);

    const sortBy = yield select(ConnectivitySelectors.activeMopSortSelector);
    const originGroupBy = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
    const destinationGroupBy = yield select(ConnectivitySelectors.activeDestinationGroupBySelector);
    const isDomesticIncluded = yield select(ConnectivitySelectors.isDomesticIncluded);
    const includeGroundTravel = yield select(ConnectivitySelectors.isGroundTravelIncludedSelector);

    const travelVolPageRes: dto.TravelVolumeListModel = yield call(
      ConnectivityAPI.getTravelVolume,
      {
        sourceLocationId: originId,
        sourceGroupBy: originGroupBy,
        destinationLocationId: destinationId,
        destinationGroupBy: destinationGroupBy,
        pageSize: MopODPageSize,
        sortBy: sortBy,
        skipRows: numOfVisibleRows,
        includeDomestic: isDomesticIncluded,
        includeAirTravel: true,
        includeGroundTravel: includeGroundTravel,
      }
    );

    yield put(appendODInfo(travelVolPageRes));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingPaginationData());
    yield showServerError;
  }
}

export function* toggleDomesticSaga(action: PayloadAction<ConnectivityToggleRequestType>) {
  try {
    yield put(toggleIncludeDomestic());

    const isDomesticIncluded = yield select(ConnectivitySelectors.isDomesticIncluded);
    const origin = yield select(ConnectivitySelectors.originLocationSelector);
    const destination = yield select(ConnectivitySelectors.destinationLocationSelector);
    const originGroupBy = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
    const destinationGroupBy = yield select(ConnectivitySelectors.activeDestinationGroupBySelector);

    // TODO: df248ed6 This next block isn't needed once Anywhere with Domestic Toggle is not on can have counties (Canada county release)
    if (!isDomesticIncluded) {
      if (origin == null && originGroupBy === dto.ConnectivityGroupByType.County) {
        yield put(
          setActiveGroupBy({
            originGroupBy: dto.ConnectivityGroupByType.Airport,
            destinationGroupBy,
          })
        );
      } else if (destination == null && destinationGroupBy === dto.ConnectivityGroupByType.County) {
        yield put(
          setActiveGroupBy({
            originGroupBy,
            destinationGroupBy: dto.ConnectivityGroupByType.Airport,
          })
        );
      }
    }

    if (action.payload === 'people') {
      yield put(startLoadingTravelVolumes());
      yield delay(500);
      yield put(fetchMopODInfo());
    } else {
      yield put(startLoadingDiseaseRisks());
      yield delay(500);
      yield put(fetchDiseaseRisk(false));
    }
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingData());
  }
}

function* fetchAggregatedDiseaseRisk(setAllDiseases: boolean) {
  try {
    yield put(startLoadingModAggRow());

    const sourceId = yield select(ConnectivitySelectors.getOriginLocationId);
    const destinationId = yield select(ConnectivitySelectors.getDestinationLocationId);
    const sourceGroup = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
    const destinationGroup = yield select(ConnectivitySelectors.activeDestinationGroupBySelector);
    const includeDomestic = yield select(ConnectivitySelectors.isDomesticIncluded);
    const diseaseIds = yield select(ConnectivitySelectors.filteredDiseasesSelector);

    const res: dto.DiseaseMovementModel = yield call(ConnectivityAPI.getAggregatedDiseaseRisk, {
      sourceLocationId: sourceId,
      destinationLocationId: destinationId,
      sourceGroupBy: sourceGroup,
      destinationGroupBy: destinationGroup,
      dateFrom: Today,
      includeDomestic: includeDomestic,
      diseaseIds: diseaseIds,
    });

    if (setAllDiseases) yield put(setModAggRowDiseases(res.diseaseRiskVolumes));
    yield put(setModAggRowInfo(res.diseaseRiskVolumes));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingModAggRow());
    yield showServerError;
  }
}

function* fetchDiseaseRiskRows(isPaginatedRequest: boolean) {
  try {
    yield put(startLoadingDiseaseRisks());

    const sourceId = yield select(ConnectivitySelectors.getOriginLocationId);
    const destinationId = yield select(ConnectivitySelectors.getDestinationLocationId);
    const sourceGroup = yield select(ConnectivitySelectors.activeOriginGroupBySelector);
    const destinationGroup = yield select(ConnectivitySelectors.activeDestinationGroupBySelector);
    const includeDomestic = yield select(ConnectivitySelectors.isDomesticIncluded);
    const diseaseIds = yield select(ConnectivitySelectors.filteredDiseasesSelector);
    const sortBy = yield select(ConnectivitySelectors.activeModSortSelector);
    const skipRows = isPaginatedRequest
      ? yield select(ConnectivitySelectors.getNumOfVisibleDiseaseRiskRows)
      : 0;

    const res: dto.DiseaseMovementListModel = yield call(ConnectivityAPI.getDiseaseRisk, {
      sourceLocationId: sourceId,
      destinationLocationId: destinationId,
      sourceGroupBy: sourceGroup,
      destinationGroupBy: destinationGroup,
      dateFrom: Today,
      includeDomestic: includeDomestic,
      diseaseIds: diseaseIds,
      pageSize: ModODPageSize,
      skipRows: skipRows,
      sortBy: sortBy,
    });

    if (isPaginatedRequest) yield put(appendPaginatedDiseaseRisks(res.diseaseMovements));
    else yield put(setDiseaseRisks(res));
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingDiseaseRisks());
    yield showServerError;
  }
}

export function* fetchDiseaseRiskSaga(action: PayloadAction<boolean>) {
  // If true we are resetting the disease risk info.
  if (action.payload === true) yield put(resetDiseaseRiskInfo());

  yield all([fetchAggregatedDiseaseRisk(true), fetchDiseaseRiskRows(false)]);
}

export function* fetchPaginatedDiseaseRiskSaga() {
  yield fetchDiseaseRiskRows(true);
}

export function* updateModGroupBySaga(action: PayloadAction<ConnectivityGroupByRequest>) {
  yield put(startRefreshingDiseaseRisks());
  yield put(setActiveGroupBy(action.payload));

  if (shouldShowGroupByDisclaimer(action.payload)) {
    yield put(setGroupByDisclaimerStatus('shown'));
  } else {
    yield put(setGroupByDisclaimerStatus('hidden'));
  }

  yield fetchDiseaseRiskRows(false);
}

export function* updateModSortSaga(action: PayloadAction<dto.DiseaseMovementSortType>) {
  yield put(startRefreshingDiseaseRisks());
  yield put(setActiveModSort(action.payload));

  yield fetchDiseaseRiskRows(false);
}

export function* updateFilteredDiseasesSaga(action: PayloadAction<number[]>) {
  yield put(startRefreshingDiseaseRisks());
  yield put(setFilteredDiseases(action.payload));
  yield all([fetchAggregatedDiseaseRisk(false), fetchDiseaseRiskRows(false)]);
}

export function* updateFilterAndDomesticSaga(
  action: PayloadAction<{ diseaseIds: number[]; isDomestic: boolean }>
) {
  const prevFilter = yield select(ConnectivitySelectors.filteredDiseasesSelector);
  const prevDomesticToggle = yield select(ConnectivitySelectors.isDomesticIncluded);

  // Don't need to set / query anything if the toggle and filters are the same
  if (
    !isEqual(prevFilter, action.payload.diseaseIds) ||
    prevDomesticToggle !== action.payload.isDomestic
  ) {
    yield put(setFilteredDiseases(action.payload.diseaseIds));
    yield put(setIncludeDomestic(action.payload.isDomestic));

    yield put(startRefreshingDiseaseRisks());
    yield put(startLoadingModAggRow());

    yield all([fetchAggregatedDiseaseRisk(false), fetchDiseaseRiskRows(false)]);
  }
}

export function* bulkUpdateModSettingsSaga(action: PayloadAction<ModBulkUpdateRequest>) {
  try {
    yield put(startRefreshingDiseaseRisks());

    const { originGroupBy, destinationGroupBy, isDomesticChecked, filteredDiseaseIds } =
      action.payload;

    yield put(setActiveGroupBy({ originGroupBy, destinationGroupBy }));
    yield put(setIncludeDomestic(isDomesticChecked));
    yield put(setFilteredDiseases(filteredDiseaseIds));

    yield all([fetchAggregatedDiseaseRisk(false), fetchDiseaseRiskRows(false)]);
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
    yield put(errorLoadingDiseaseRisks());
    yield showServerError;
  }
}

function* connectivitySaga() {
  yield takeLatest(fetchConnectivityLocationInfo, fetchConnectivityLocationInfoSaga);
  yield takeLatest(toggleDomestic, toggleDomesticSaga);

  yield takeLatest(fetchMopODInfo, fetchMopODInfoSaga);
  yield takeLatest(fetchPaginatedMopODInfo, fetchPaginatedMopODInfoSaga);
  yield takeLatest(updateMopSort, updateMopSortSaga);
  yield takeLatest(updateMopGroupBy, updateMopGroupBySaga);
  yield takeLatest(toggleMopGroundTravel, toggleMopGroundTravelSaga);
  yield takeLatest(bulkUpdateMopSettings, bulkUpdateMopSettingsSaga);

  yield takeLatest(fetchDiseaseRisk, fetchDiseaseRiskSaga);
  yield takeLatest(fetchPaginatedDiseaseRisk, fetchPaginatedDiseaseRiskSaga);
  yield takeLatest(updateModGroupBy, updateModGroupBySaga);
  yield takeLatest(updateModSort, updateModSortSaga);
  yield takeLatest(updateFilteredDiseases, updateFilteredDiseasesSaga);
  yield takeLatest(updateFilterAndDomestic, updateFilterAndDomesticSaga);
  yield takeLatest(bulkUpdateModSettings, bulkUpdateModSettingsSaga);
}

export default connectivitySaga;
